import Head from 'next/head';
import { Blog } from 'components/blog/blog.types';
import { LIBUM_IMAGE_PARTIAL_URL, LIBUM_URL, LINKEDIN_URL, GITHUB_URL, DISCORD_URL } from 'shared/constants/globals';

type ProductSchemaData = {
  name: string;
  description: string;
  category: string;
  operatingSystem: string;
  image?: string;
};

type Breadcrumb = {
  name: string;
  url: string;
};

type ContainerProps = {
  title: string;
  description: string;
  keywords: string;
  url: string;
  socialCard: string;
  children: React.ReactNode;
  product?: ProductSchemaData;
  breadcrumbs?: Breadcrumb[];
  blog?: Blog;
  noIndex?: boolean;
};

export default function Container({
  title,
  description,
  keywords,
  url,
  socialCard,
  children,
  product,
  breadcrumbs,
  blog,
  noIndex,
}: ContainerProps) {
  const socialCardImage = `${LIBUM_IMAGE_PARTIAL_URL}_${socialCard}.png`;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Libum',
    url: LIBUM_URL,
    logo: `${LIBUM_URL}/images/libum-logo.png`,
    description:
      'Libum is a software company driving the modernization of credit unions with innovative tools for Jack Henry Symitar platforms.',
    sameAs: [LINKEDIN_URL, GITHUB_URL, DISCORD_URL],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: 'sales@libum.io',
    },
  };

  const softwareAppSchema = product
    ? {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: product.name,
        description: product.description,
        url,
        image: product.image || socialCardImage,
        applicationCategory: product.category,
        operatingSystem: product.operatingSystem,
        brand: {
          '@type': 'Brand',
          name: 'Libum',
        },
        publisher: {
          '@type': 'Organization',
          name: 'Libum',
          url: LIBUM_URL,
        },
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          seller: {
            '@type': 'Organization',
            name: 'Libum',
          },
        },
      }
    : null;

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Libum',
    url: LIBUM_URL,
  };

  const siteNavigationSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'SiteNavigationElement',
      name: 'Products',
      url: `${LIBUM_URL}/products`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SiteNavigationElement',
      name: 'PowerOn Pro',
      url: `${LIBUM_URL}/poweron-pro`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SiteNavigationElement',
      name: 'PowerOn Pipelines',
      url: `${LIBUM_URL}/poweron-pipelines`,
    },
    { '@context': 'https://schema.org', '@type': 'SiteNavigationElement', name: 'NexumAPI', url: `${LIBUM_URL}/nexum` },
    { '@context': 'https://schema.org', '@type': 'SiteNavigationElement', name: 'Glaci', url: `${LIBUM_URL}/glaci` },
    { '@context': 'https://schema.org', '@type': 'SiteNavigationElement', name: 'About', url: `${LIBUM_URL}/about` },
    { '@context': 'https://schema.org', '@type': 'SiteNavigationElement', name: 'Blog', url: `${LIBUM_URL}/blog` },
  ];

  const breadcrumbSchema = breadcrumbs
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: LIBUM_URL },
          ...breadcrumbs.map((bc, i) => ({
            '@type': 'ListItem',
            position: i + 2,
            name: bc.name,
            item: bc.url,
          })),
        ],
      }
    : null;

  const blogPostingSchema = blog
    ? {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: blog.title,
        description: blog.description,
        datePublished: blog.date.toISOString().split('T')[0],
        author: { '@type': 'Person', name: blog.author, url: blog.authorLink },
        publisher: { '@type': 'Organization', name: 'Libum', url: LIBUM_URL },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${LIBUM_URL}/blog/${blog.id}` },
        image: socialCardImage,
      }
    : null;

  return (
    <>
      <Head>
        <link rel="icon" href="/images/favicon.ico" sizes="48x48" />
        <link rel="icon" type="image/svg+xml" href="/images/favicon.svg" />
        <link rel="icon" type="image/png" sizes="96x96" href="/images/favicon-96x96.png" />
        <link rel="icon" type="image/png" sizes="48x48" href="/images/favicon-48x48.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/images/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/images/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/images/apple-touch-icon.png" />
        <meta name="apple-mobile-web-app-title" content="Libum" />
        <link rel="manifest" href="/site.webmanifest" />
        <title>{title}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
        <meta name="description" content={description} />
        {noIndex && <meta name="robots" content="noindex, nofollow" />}
        <link rel="canonical" href={url} />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:site_name" content="Libum" />
        {keywords !== '' && <meta property="og:keywords" content={keywords} />}
        <meta property="og:image" content={socialCardImage} />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={socialCardImage} />
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(siteNavigationSchema) }} />
        {softwareAppSchema && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareAppSchema) }} />
        )}
        {breadcrumbSchema && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
        )}
        {blogPostingSchema && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogPostingSchema) }} />
        )}
      </Head>
      {children}
    </>
  );
}
